import {
  IBusinessCustomVariablesValues,
  IOption,
} from 'app/interfaces/customVariablesInterfaces';
import {
  EFunctions,
  EIncorporationType,
  IBankAccount,
  PixType,
} from 'modules/customer/context/CustomerProvider/customer.interfaces';
import { IAddress } from './Address/types';
import { IBusinessPersonRelation } from 'modules/customer/services/api/interfaces';

export interface IIdentification {
  document_issue_date?: string | null;
  document_issuer?: string;
  document_number?: string;
  document_type?: IOption;
}

export enum EEducationLevel {
  NOT_INFORMED = 'NOT_INFORMED',
  ILLITERATE = 'ILLITERATE',
  LITERATE_WITHOUT_FORMAL_EDUCATION = 'LITERATE_WITHOUT_FORMAL_EDUCATION',
  PRIMARY_INCOMPLETE = 'PRIMARY_INCOMPLETE',
  PRIMARY_COMPLETE = 'PRIMARY_COMPLETE',
  SECONDARY_INCOMPLETE = 'SECONDARY_INCOMPLETE',
  SECONDARY_COMPLETE = 'SECONDARY_COMPLETE',
  HIGHER_INCOMPLETE = 'HIGHER_INCOMPLETE',
  HIGHER_COMPLETE = 'HIGHER_COMPLETE',
}

export enum EEducationLevelDescription {
  NOT_INFORMED = 'Não informado',
  ILLITERATE = 'Analfabeto',
  LITERATE_WITHOUT_FORMAL_EDUCATION = 'Alfabetizado sem escolaridade',
  PRIMARY_INCOMPLETE = 'Ensino fundamental incompleto',
  PRIMARY_COMPLETE = 'Ensino fundamental completo',
  SECONDARY_INCOMPLETE = 'Ensino médio incompleto',
  SECONDARY_COMPLETE = 'Ensino médio completo',
  HIGHER_INCOMPLETE = 'Ensino superior incompleto',
  HIGHER_COMPLETE = 'Ensino superior completo',
}

export interface IPersonForm {
  address?: IAddress;
  birth_date?: string;
  birthplace?: string;
  identification?: IIdentification;
  email_address?: string;
  full_name?: string;
  marital_status?: IOption;
  monthly_income?: string;
  mothers_name?: string;
  nationality?: string;
  occupation?: string;
  pep?: IOption;
  phone?: string;
  role?: IOption;
  sex?: IOption;
  taxpayer_id?: string;
  education_level?: EEducationLevel;

  // representatives options
  signer?: IOption;
  workRelation?: IOption;
  share?: number;
  aditional_information?: string;
  id?: string;

  // bank info
  external_bank_account?: IBankAccount;
  pix?: {
    key: string;
    key_type: PixType;
  };

  // marital info
  spouse_full_name?: string;
  spouse_taxpayer_id?: string;
  spouse_date_of_birth?: string;
  marital_property_system?: IOption;
}

export interface IOriginator {
  id: string;
  legal_name: string;
}

export interface IPhone {
  area_code: string;
  country_code: string;
  number: string;
  formatted_number?: string;
}

export interface IPerson {
  id: string;
  full_name: string;
  taxpayer_id: string;
  birth_date: string;
  birthplace: string | null;
  mothers_name: string | null;
  nationality: string;
  occupation: string;
  email_address: string;
  phone: IPhone;
  monthly_income: number;
  marital_status: string | null;
  marital_property_system: string | null;
  spouse_full_name: string | null;
  spouse_taxpayer_id: string | null;
  spouse_date_of_birth: string | null;
  sex: string | null;
  pep: boolean;
  address: IAddress;
  employer: string | null;
  external_bank_account?: IBankAccount | null;
  pix?: {
    key?: string;
    key_type?: PixType;
  } | null;
  scr_evidence_provider: string | null;
  scr_evidence_type: string | null;
  has_scr_evidence: boolean;
  income_range: string | null;
  id_document: string | null;
  custom_variables: Record<string, string>;
  originator: IOriginator;
  created_at: string;
  version: number;

  // representatives options
  signer?: IOption;
  workRelation?: IOption;
  share?: number;
  aditional_information?: string;
  role?: string;
}

export interface ICompanyForm {
  id?: string;
  address: IAddress;
  billing_address?: IAddress;
  customVariables?: IBusinessCustomVariablesValues[];
  email_address: string;
  foundation_date: string;
  fundingId?: string;
  hasQualificationRequest?: boolean;
  industry_classification?: string;
  legal_name: string;
  monthly_revenue?: string;
  name?: string;
  phone: string;
  productId?: string;
  role?: EFunctions;
  share_capital?: string;
  taxpayer_id: string;
  tax_regime?: {
    label: string;
    value: string;
  };
  incorporation_type?: {
    label: string;
    value: EIncorporationType | undefined;
  };
  representatives: IPersonForm[] | IBusinessPersonRelation[];

  // bank info
  external_bank_account?: IBankAccount;
  pix?: {
    key: string;
    key_type: PixType;
  };
}

interface Phone {
  country_code: string;
  area_code: string;
  number: string;
  formatted_number: string;
}

export interface ICompanyResponse {
  address?: IAddress;
  billing_address?: IAddress;
  created_at?: string;
  custodian?: boolean;
  custom_variables?: Record<string, string>;
  email_address?: string;
  employer?: boolean;
  external_bank_account?: IBankAccount | null;
  foundation_date?: string;
  has_scr_evidence?: boolean;
  id?: string;
  income_range?: string;
  incorporation_type?: string;
  industry_classification?: string;
  legal_name?: string;
  monthly_revenue?: number;
  name?: string | null;
  phone?: Phone;
  pix?: {
    key?: string;
    key_type?: PixType;
  } | null;
  scr_evidence_provider?: string;
  scr_evidence_type?: string;
  share_capital?: number;
  tax_regime?: string;
  taxpayer_id?: string;
  version?: number;
}

export const workRelationOptions = [
  { value: 'EMPLOYEE', label: 'Empregado' },
  { value: 'MANAGER', label: 'Administrador' },
  { value: 'PARTNER', label: 'Sócio' },
  { value: 'PROXY', label: 'Procurador' },
  { value: 'OTHER', label: 'Não especificado' },
];

export const educationLevelOptions = [
  {
    value: EEducationLevel.NOT_INFORMED,
    label: EEducationLevelDescription.NOT_INFORMED,
  },
  {
    value: EEducationLevel.ILLITERATE,
    label: EEducationLevelDescription.ILLITERATE,
  },
  {
    value: EEducationLevel.LITERATE_WITHOUT_FORMAL_EDUCATION,
    label: EEducationLevelDescription.LITERATE_WITHOUT_FORMAL_EDUCATION,
  },
  {
    value: EEducationLevel.PRIMARY_INCOMPLETE,
    label: EEducationLevelDescription.PRIMARY_INCOMPLETE,
  },
  {
    value: EEducationLevel.PRIMARY_COMPLETE,
    label: EEducationLevelDescription.PRIMARY_COMPLETE,
  },
  {
    value: EEducationLevel.SECONDARY_INCOMPLETE,
    label: EEducationLevelDescription.SECONDARY_INCOMPLETE,
  },
  {
    value: EEducationLevel.SECONDARY_COMPLETE,
    label: EEducationLevelDescription.SECONDARY_COMPLETE,
  },
  {
    value: EEducationLevel.HIGHER_INCOMPLETE,
    label: EEducationLevelDescription.HIGHER_INCOMPLETE,
  },
  {
    value: EEducationLevel.HIGHER_COMPLETE,
    label: EEducationLevelDescription.HIGHER_COMPLETE,
  },
];
